import { DropdownMenu } from "@/components/ai/prompt-input/DropdownMenu";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/flexkit/Command";
import { cn } from "@/components/podkit/lib/cn";
import { type PlainProject } from "@/queries/project-queries";

import IconArchive from "central-icons/IconArchive";
import IconArrowLeft from "central-icons/IconArrowLeft";
import IconBlankPagePortrait from "central-icons/IconBlankPagePortrait";
import IconWorld from "central-icons/IconWorld";
import { useCommandState } from "cmdk";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import type React from "react";
import { memo, useEffect, useMemo, useRef } from "react";
import { useStore } from "jotai";
import { atomEffect } from "jotai-effect";
import type { SelectedContext } from "@/components/ai/prompt-input/NewPromptInput";
import { ProjectMenu } from "./ProjectMenu";
import UrlSelectorMenu from "./UrlSelectorMenu";
import {
    MENU_KIND,
    type MenuKind,
    errorAtom,
    searchQueryAtom,
    menuKindAtom,
    isFixedMenuKindAtom,
    canGoBackAtom,
    clearSearchQueryAtomEffect,
    clearErrorAtomEffect,
    isParsingUrlAtom,
    pendingContextUrlAtom,
} from "./state";

type ContextSelectorMenuProps = {
    width: number;
    kind?: MenuKind;
    onSelectContext: OnSelectContextCallback;
};

export type OnSelectContextCallback = (context: SelectedContext[], associatedProjects: PlainProject[]) => void;

const ContextSelectorMenu = ({ width, kind, onSelectContext }: ContextSelectorMenuProps) => {
    const setMenuKind = useSetAtom(menuKindAtom);
    const menuKind = useAtomValue(menuKindAtom);
    const setIsFixedMenuKind = useSetAtom(isFixedMenuKindAtom);
    const setError = useSetAtom(errorAtom);
    const setSearchQuery = useSetAtom(searchQueryAtom);
    useAtom(clearSearchQueryAtomEffect);
    useAtom(clearErrorAtomEffect);

    useEffect(() => {
        if (kind) {
            setIsFixedMenuKind(true);
            setMenuKind(kind);
        } else {
            setIsFixedMenuKind(false);
            setMenuKind(MENU_KIND.options);
        }
    }, [kind, setMenuKind, setIsFixedMenuKind]);

    useEffect(
        () => () => {
            setMenuKind(MENU_KIND.options);
            setError(null);
            setSearchQuery("");
        },
        [setMenuKind, setError, setSearchQuery],
    );

    return (
        <DropdownMenu width={width}>
            <Command key={menuKind}>
                <AutoSwitchMenu>
                    <div className="flex w-full items-center border-b border-border-base">
                        <BackButton />
                        <SearchInput />
                    </div>
                    <ActiveMenu onSelectContext={onSelectContext} />
                </AutoSwitchMenu>
            </Command>
        </DropdownMenu>
    );
};
ContextSelectorMenu.Kind = MENU_KIND;

const AutoSwitchMenu = memo<React.PropsWithChildren>(({ children }) => {
    const query = useAtomValue(searchQueryAtom);
    const isAllFiltered = useCommandState((state) => state.filtered.count === 0);
    const setMenuKind = useSetAtom(menuKindAtom);
    const setSearchQuery = useSetAtom(searchQueryAtom);
    const store = useStore();

    useEffect(() => {
        const currentMenuKind = store.get(menuKindAtom);
        if (isAllFiltered && query) {
            try {
                // test if the query is a valid url by trying to construct a URL object with it
                // URL constructor throws an error if the query is not a valid url
                new URL(query);
                if (currentMenuKind !== MENU_KIND.url) {
                    setMenuKind(MENU_KIND.url);
                }
            } catch {
                if (currentMenuKind !== MENU_KIND.project) {
                    setMenuKind(MENU_KIND.project);
                }
            }
            setSearchQuery(query);
        }
    }, [isAllFiltered, query, setMenuKind, setSearchQuery, store]);

    return children;
});
AutoSwitchMenu.displayName = "AutoSwitchMenu";

const BackButton = memo(() => {
    const canGoBack = useAtomValue(canGoBackAtom);
    const setMenuKind = useSetAtom(menuKindAtom);

    if (!canGoBack) return null;

    return (
        <button
            type="button"
            className="ml-3 rounded text-content-muted hover:bg-surface-hover"
            onClick={() => {
                setMenuKind(MENU_KIND.options);
            }}
            data-tracking-id="context-selector-menu-back-button"
        >
            <span className="flex size-6 items-center justify-center">
                <IconArrowLeft className="size-4 shrink-0 text-content-muted" />
            </span>
            <span className="sr-only">All context options</span>
        </button>
    );
});
BackButton.displayName = "BackButton";

const SearchInput = () => {
    const [query, setQuery] = useAtom(searchQueryAtom);
    const menuKind = useAtomValue(menuKindAtom);
    const canGoBack = useAtomValue(canGoBackAtom);
    const isParsingUrl = useAtomValue(isParsingUrlAtom);
    const pendingContextUrl = useAtomValue(pendingContextUrlAtom);
    const inputRef = useRef<HTMLInputElement>(null);

    // when clicking outside the search input to change menu, the search input loses focus
    // this atom effect auto focuses the search input whenever the menu changes
    const autoFocusEffect = useMemo(
        () =>
            atomEffect((get) => {
                get(menuKindAtom);
                inputRef.current?.focus();
            }),
        [],
    );
    useAtom(autoFocusEffect);

    let placeholder: string;
    switch (menuKind) {
        case MENU_KIND.options:
            placeholder = "Type to search…";
            break;
        case MENU_KIND.project:
            placeholder = "Find projects…";
            break;
        case MENU_KIND.url:
            placeholder = "https://github.com/gitpod-io/gitpod";
            break;
    }

    return (
        <CommandInput
            ref={inputRef}
            placeholder={placeholder}
            value={query}
            disabled={isParsingUrl || !!pendingContextUrl}
            onValueChange={setQuery}
            hideIcon={canGoBack}
            containerClassName={cn("w-full border-b-0", {
                "-ml-1": canGoBack,
            })}
        />
    );
};

const ActiveMenu = memo(({ onSelectContext }: { onSelectContext: OnSelectContextCallback }) => {
    const menuKind = useAtomValue(menuKindAtom);
    switch (menuKind) {
        default:
            return null;
        case MENU_KIND.options:
            return <OptionsMenu onSelectContext={onSelectContext} />;
        case MENU_KIND.project:
            return <ProjectMenu onSelectContext={onSelectContext} />;
        case MENU_KIND.url:
            return <UrlSelectorMenu onSelectContext={onSelectContext} />;
    }
});
ActiveMenu.displayName = "ActiveMenu";

const OptionsMenu = memo(({ onSelectContext }: { onSelectContext: OnSelectContextCallback }) => {
    const setMenuKind = useSetAtom(menuKindAtom);
    const setSearchQuery = useSetAtom(searchQueryAtom);

    const openMenu = (menuKind: MenuKind) => {
        setSearchQuery("");
        setMenuKind(menuKind);
    };

    return (
        <CommandList>
            <CommandEmpty className="px-3 py-2">No options found</CommandEmpty>
            <CommandGroup heading="Select an option">
                <CommandItem
                    onSelect={() => {
                        openMenu(MENU_KIND.project);
                    }}
                >
                    <IconArchive className="size-4 shrink-0" />
                    Start from project
                </CommandItem>
                <CommandItem
                    onSelect={() => {
                        openMenu(MENU_KIND.url);
                    }}
                >
                    <IconWorld className="size-4 shrink-0" />
                    Start from Git URL
                </CommandItem>
                <CommandItem
                    onSelect={() => {
                        onSelectContext([{ kind: "scratch" }], []);
                    }}
                >
                    <IconBlankPagePortrait className="size-4 shrink-0" />
                    Start from scratch
                </CommandItem>
            </CommandGroup>
        </CommandList>
    );
});
OptionsMenu.displayName = "OptionsMenu";

export default ContextSelectorMenu;
